import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { GatewayAPIError } from "../../app/api/client";
import { authStore } from "../auth/authStore";
import { IconClose, IconRefresh, IconSend, IconTrash, IconUserPlus } from "../chats/chatIcons";
import { resolveUserIDByIdentifier } from "../chats/chatApi";
import { searchDiscovery as searchUserDiscovery } from "../discovery/discoveryApi";
import type { DiscoveryResult } from "../discovery/discoveryApi";
import { runtimeI18n } from "../i18n/i18n";
import {
  ContactSummary,
  addContact as addSavedContact,
  listContacts as listSavedContacts,
  removeContact as removeSavedContact,
} from "./contactApi";

export interface ContactsPanelAPI {
  listContacts: () => Promise<ContactSummary[]>;
  addContact: (userID: string) => Promise<ContactSummary>;
  removeContact: (userID: string) => Promise<void>;
  resolveUserIDByIdentifier: (identifier: string) => Promise<string>;
  searchDiscovery: (query: string) => Promise<DiscoveryResult[]>;
}

export interface ContactsPanelProps {
  open?: boolean;
  initialContacts?: ContactSummary[];
  autoLoad?: boolean;
  api?: ContactsPanelAPI;
  onClose: () => void;
  onStartChat: (userID: string) => Promise<void> | void;
  onAuthRequired?: () => void;
}

interface FeedbackState {
  tone: "error" | "success";
  text: string;
}

type ContactTranslator = (key: string, options?: Record<string, string>) => string;

export const CONTACTS_PANEL_CLASSES = {
  root: "contacts-panel",
  list: "contacts-list",
  row: "contact-row",
  avatar: "contact-avatar",
  content: "contact-content",
  actions: "contact-actions",
} as const;

const defaultContactsPanelAPI: ContactsPanelAPI = {
  listContacts: () => listSavedContacts(),
  addContact: (userID) => addSavedContact(userID),
  removeContact: (userID) => removeSavedContact(userID),
  resolveUserIDByIdentifier: (identifier) => resolveUserIDByIdentifier(identifier),
  searchDiscovery: (query) => searchUserDiscovery(query),
};

function normalizeContactUserID(userID: string): string {
  const normalized = userID.trim();
  if (normalized === "") {
    throw new Error("user_id must not be empty");
  }
  if (!normalized.startsWith("usr_") || normalized.includes("/") || /\s/.test(normalized)) {
    throw new Error("user_id must be a canonical user id");
  }
  return normalized;
}

export function normalizeContactIdentifierInput(value: string): string {
  const normalized = value.trim();
  if (normalized === "") {
    throw new Error("identifier must not be empty");
  }
  return normalized;
}

export function isCanonicalContactUserID(value: string): boolean {
  const normalized = value.trim();
  return normalized.startsWith("usr_") && !normalized.includes("/") && !/\s/.test(normalized);
}

function isSafePublicUsernameAlias(value: string): boolean {
  if (value.includes("@") || value.startsWith("+")) {
    return false;
  }
  if (/\s|[/\\]/.test(value)) {
    return false;
  }
  if (/^\d[\d.()_-]*$/.test(value)) {
    return false;
  }
  return value !== "";
}

function getSafeContactUsername(contact: ContactSummary): string {
  const username = contact.username?.trim() ?? "";
  return isSafePublicUsernameAlias(username) ? username : "";
}

function isNicknameContactCandidate(value: string): boolean {
  const normalized = value.trim();
  if (normalized.includes("@") && !normalized.startsWith("@")) {
    return false;
  }
  const candidate = normalized.startsWith("@") ? normalized.slice(1) : normalized;
  const candidateLength = Array.from(candidate).length;
  return candidateLength >= 3 && candidateLength <= 32 && /^[\p{L}\p{N}_.]+$/u.test(candidate);
}

function shouldTryNicknameDiscovery(error: unknown): boolean {
  return (
    error instanceof GatewayAPIError &&
    (error.code === "user_not_found" || error.code === "not_found" || error.code === "invalid_request")
  );
}

function nicknameDiscoveryNotFound(): GatewayAPIError {
  return new GatewayAPIError(404, "user_not_found", { error: "user_not_found" });
}

function findFirstCanonicalDiscoveryUserID(results: DiscoveryResult[]): string | null {
  const userResult = results.find((result) => {
    return result.kind === "user" && isCanonicalContactUserID(result.user_id);
  });
  return userResult?.kind === "user" ? userResult.user_id.trim() : null;
}

export function getContactDisplayName(contact: ContactSummary): string {
  const displayName = contact.display_name.trim();
  if (displayName !== "") {
    return displayName;
  }
  const nickname = contact.nickname?.trim() ?? "";
  if (nickname !== "") {
    return nickname;
  }
  return getSafeContactUsername(contact) || contact.user_id;
}

export function getContactSecondaryLabel(contact: ContactSummary): string {
  const primary = getContactDisplayName(contact);
  const displayName = contact.display_name.trim();
  if (displayName !== "" && displayName !== primary) {
    return displayName;
  }
  const nickname = contact.nickname?.trim() ?? "";
  if (nickname !== "" && nickname !== primary) {
    return nickname;
  }
  const username = getSafeContactUsername(contact);
  if (username !== "" && username !== primary) {
    return username;
  }
  return contact.user_id;
}

export function getContactAccessibleLabel(contact: ContactSummary): string {
  return contact.display_name.trim() || contact.nickname?.trim() || contact.user_id;
}

export function deriveContactInitials(contact: ContactSummary): string {
  const displayName = getContactDisplayName(contact);
  const parts = displayName.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? contact.user_id[0] ?? "#";
  const second = parts.length > 1 ? parts[1]?.[0] : undefined;
  return `${first}${second ?? ""}`.toUpperCase();
}

export function sortContactSummaries(contacts: ContactSummary[]): ContactSummary[] {
  return [...contacts].sort((left, right) => {
    const leftKey = [getContactDisplayName(left).toLowerCase(), getContactSecondaryLabel(left).toLowerCase(), left.user_id];
    const rightKey = [getContactDisplayName(right).toLowerCase(), getContactSecondaryLabel(right).toLowerCase(), right.user_id];
    for (let index = 0; index < leftKey.length; index += 1) {
      const comparison = (leftKey[index] ?? "").localeCompare(rightKey[index] ?? "");
      if (comparison !== 0) {
        return comparison;
      }
    }
    return 0;
  });
}

export function upsertContactSummary(contacts: ContactSummary[], contact: ContactSummary): ContactSummary[] {
  const byUserID = new Map<string, ContactSummary>();
  for (const existing of contacts) {
    byUserID.set(existing.user_id, existing);
  }
  byUserID.set(contact.user_id, contact);
  return sortContactSummaries([...byUserID.values()]);
}

export function mapContactPanelErrorMessage(
  error: unknown,
  fallbackKey = "contacts.errors.generic",
  translate: ContactTranslator = (key, options) => runtimeI18n.t(key, options),
): string {
  if (!(error instanceof GatewayAPIError)) {
    return translate(fallbackKey);
  }

  const messageKey =
    error.code === "contact_already_exists"
      ? "contacts.errors.duplicate"
      : error.code === "user_not_found" || error.code === "not_found"
        ? "contacts.errors.notFound"
        : error.code === "contact_not_found"
          ? "contacts.errors.contactNotFound"
          : error.code === "invalid_user_id" || error.code === "invalid_request"
            ? "contacts.errors.invalid"
            : error.code === "self_contact"
              ? "contacts.errors.self"
              : error.code === "unauthorized"
                ? "contacts.errors.unauthorized"
                : error.code === "forbidden"
                  ? "contacts.errors.forbidden"
                  : fallbackKey;
  return translate(messageKey);
}

export async function loadContactsForPanel(api: Pick<ContactsPanelAPI, "listContacts">): Promise<ContactSummary[]> {
  return sortContactSummaries(await api.listContacts());
}

export async function addContactFromIdentifier(
  identifier: string,
  api: Pick<ContactsPanelAPI, "addContact" | "resolveUserIDByIdentifier" | "searchDiscovery">,
): Promise<ContactSummary> {
  const normalizedIdentifier = normalizeContactIdentifierInput(identifier);
  let userID = normalizedIdentifier;
  if (!isCanonicalContactUserID(normalizedIdentifier)) {
    try {
      userID = await api.resolveUserIDByIdentifier(normalizedIdentifier);
    } catch (error) {
      if (!isNicknameContactCandidate(normalizedIdentifier) || !shouldTryNicknameDiscovery(error)) {
        throw error;
      }
      const results = await api.searchDiscovery(normalizedIdentifier);
      const discoveredUserID = findFirstCanonicalDiscoveryUserID(results);
      if (discoveredUserID === null) {
        throw nicknameDiscoveryNotFound();
      }
      userID = discoveredUserID;
    }
  }
  return api.addContact(normalizeContactUserID(userID));
}

export async function removeContactForPanel(
  userID: string,
  api: Pick<ContactsPanelAPI, "removeContact">,
): Promise<string> {
  const normalizedUserID = normalizeContactUserID(userID);
  await api.removeContact(normalizedUserID);
  return normalizedUserID;
}

export async function startContactChatFromPanel(
  contact: ContactSummary,
  onStartChat: ContactsPanelProps["onStartChat"],
): Promise<void> {
  await onStartChat(normalizeContactUserID(contact.user_id));
}

function isDirectRenderableAvatarURL(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return (
    normalized.startsWith("http://") ||
    normalized.startsWith("https://") ||
    normalized.startsWith("blob:") ||
    normalized.startsWith("data:")
  );
}

export function ContactsPanel({
  open = true,
  initialContacts = [],
  autoLoad = true,
  api = defaultContactsPanelAPI,
  onClose,
  onStartChat,
  onAuthRequired,
}: ContactsPanelProps) {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState<ContactSummary[]>(() => sortContactSummaries(initialContacts));
  const [loading, setLoading] = useState(autoLoad);
  const [lookupInput, setLookupInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [removingUserID, setRemovingUserID] = useState<string | null>(null);
  const [startingUserID, setStartingUserID] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const [avatarSources, setAvatarSources] = useState<Record<string, string>>({});
  const [failedAvatarUsers, setFailedAvatarUsers] = useState<Record<string, true>>({});
  const avatarSourcesRef = useRef<Record<string, string>>({});
  const avatarHydrationRef = useRef<Set<string>>(new Set());
  const translateContacts = useCallback<ContactTranslator>((key, options) => t(key, options), [t]);

  const handleAuthError = useCallback(
    (error: unknown) => {
      if (error instanceof GatewayAPIError && error.code === "unauthorized") {
        onAuthRequired?.();
      }
    },
    [onAuthRequired],
  );

  const reloadContacts = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const nextContacts = await loadContactsForPanel(api);
      setContacts(nextContacts);
    } catch (error) {
      handleAuthError(error);
      setFeedback({
        tone: "error",
        text: mapContactPanelErrorMessage(error, "contacts.errors.load", translateContacts),
      });
    } finally {
      setLoading(false);
    }
  }, [api, handleAuthError]);

  useEffect(() => {
    if (!autoLoad) {
      setLoading(false);
      return;
    }

    void reloadContacts();
  }, [autoLoad, reloadContacts]);

  useEffect(() => {
    avatarSourcesRef.current = avatarSources;
  }, [avatarSources]);

  useEffect(() => {
    const accessToken = authStore.getAccessToken();
    if (accessToken === null) {
      return;
    }

    for (const contact of contacts) {
      const avatarURL = contact.avatar_url?.trim() ?? "";
      if (avatarURL === "" || isDirectRenderableAvatarURL(avatarURL)) {
        continue;
      }
      if (avatarSourcesRef.current[contact.user_id] !== undefined || avatarHydrationRef.current.has(contact.user_id)) {
        continue;
      }

      const userID = contact.user_id;
      avatarHydrationRef.current.add(userID);
      void (async () => {
        try {
          const response = await fetch(avatarURL, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          if (!response.ok) {
            return;
          }
          const binary = await response.blob();
          if (binary.size <= 0) {
            return;
          }
          const sourceURL = URL.createObjectURL(binary);
          setAvatarSources((current) => {
            const previous = current[userID];
            if (previous === sourceURL) {
              return current;
            }
            if (previous !== undefined && previous.startsWith("blob:")) {
              URL.revokeObjectURL(previous);
            }
            return {
              ...current,
              [userID]: sourceURL,
            };
          });
        } catch {
          return;
        } finally {
          avatarHydrationRef.current.delete(userID);
        }
      })();
    }
  }, [contacts]);

  useEffect(() => {
    return () => {
      for (const sourceURL of Object.values(avatarSourcesRef.current)) {
        if (sourceURL.startsWith("blob:")) {
          URL.revokeObjectURL(sourceURL);
        }
      }
      avatarHydrationRef.current.clear();
    };
  }, []);

  const onAddContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAdding(true);
    setFeedback(null);
    try {
      const contact = await addContactFromIdentifier(lookupInput, api);
      setContacts((current) => upsertContactSummary(current, contact));
      setLookupInput("");
      setFeedback({
        tone: "success",
        text: t("contacts.states.added"),
      });
    } catch (error) {
      handleAuthError(error);
      setFeedback({
        tone: "error",
        text: mapContactPanelErrorMessage(error, "contacts.errors.add", translateContacts),
      });
    } finally {
      setAdding(false);
    }
  };

  const onRemoveContact = async (contact: ContactSummary) => {
    setRemovingUserID(contact.user_id);
    setFeedback(null);
    try {
      const removedUserID = await removeContactForPanel(contact.user_id, api);
      setContacts((current) => current.filter((entry) => entry.user_id !== removedUserID));
      setFeedback({
        tone: "success",
        text: t("contacts.states.removed"),
      });
    } catch (error) {
      handleAuthError(error);
      setFeedback({
        tone: "error",
        text: mapContactPanelErrorMessage(error, "contacts.errors.remove", translateContacts),
      });
    } finally {
      setRemovingUserID(null);
    }
  };

  const onStartContactChat = async (contact: ContactSummary) => {
    setStartingUserID(contact.user_id);
    setFeedback(null);
    try {
      await startContactChatFromPanel(contact, onStartChat);
    } catch (error) {
      handleAuthError(error);
      setFeedback({
        tone: "error",
        text: mapContactPanelErrorMessage(error, "contacts.errors.startChat", translateContacts),
      });
    } finally {
      setStartingUserID(null);
    }
  };

  const disabled = loading || adding;

  return (
    <section
      className={`${CONTACTS_PANEL_CLASSES.root}${open ? " contacts-panel-open" : ""}`}
      data-testid="contacts-panel"
      aria-hidden={!open}
    >
      <header className="contacts-panel-head">
        <div className="chat-shell-heading">
          <p className="chat-shell-eyebrow">{t("contacts.panel.eyebrow")}</p>
          <h2 className="chat-section-title">{t("contacts.panel.title")}</h2>
        </div>
        <div className="chat-head-actions">
          <button
            className="icon-button icon-button-ghost"
            type="button"
            onClick={() => void reloadContacts()}
            aria-label={t("contacts.panel.reload")}
            title={t("contacts.panel.reload")}
            disabled={loading}
          >
            <IconRefresh />
          </button>
          <button
            className="icon-button icon-button-ghost"
            type="button"
            onClick={onClose}
            aria-label={t("contacts.panel.close")}
            title={t("contacts.panel.close")}
          >
            <IconClose />
          </button>
        </div>
      </header>

      <form className="contacts-add-form" onSubmit={onAddContact}>
        <label className="chat-field contacts-lookup-field">
          <span>{t("contacts.lookup.label")}</span>
          <input
            data-testid="contacts-lookup-input"
            value={lookupInput}
            onChange={(event) => setLookupInput(event.target.value)}
            placeholder={t("contacts.lookup.placeholder")}
            autoComplete="off"
            disabled={disabled}
          />
        </label>
        <button
          className="icon-button icon-button-primary"
          type="submit"
          data-testid="contacts-add-submit"
          aria-label={adding ? t("contacts.actions.adding") : t("contacts.actions.add")}
          title={adding ? t("contacts.actions.adding") : t("contacts.actions.add")}
          disabled={disabled || lookupInput.trim() === ""}
        >
          <IconUserPlus />
        </button>
      </form>

      {feedback !== null ? (
        <p
          className={`status-pill contacts-feedback contacts-feedback-${feedback.tone}`}
          data-testid="contacts-feedback"
          aria-live="polite"
        >
          {feedback.text}
        </p>
      ) : null}

      {loading ? (
        <p className="muted-line" data-testid="contacts-loading">
          {t("contacts.states.loading")}
        </p>
      ) : null}

      {!loading && contacts.length === 0 ? (
        <p className="muted-line" data-testid="contacts-empty">
          {t("contacts.states.empty")}
        </p>
      ) : null}

      <ul className={CONTACTS_PANEL_CLASSES.list}>
        {contacts.map((contact) => {
          const title = getContactDisplayName(contact);
          const accessibleName = getContactAccessibleLabel(contact);
          const secondary = getContactSecondaryLabel(contact);
          const status = contact.status_text?.trim() ?? "";
          const directAvatar = contact.avatar_url !== undefined && isDirectRenderableAvatarURL(contact.avatar_url);
          const avatarSource =
            failedAvatarUsers[contact.user_id] === true
              ? ""
              : directAvatar
                ? contact.avatar_url ?? ""
                : avatarSources[contact.user_id] ?? "";
          const removing = removingUserID === contact.user_id;
          const starting = startingUserID === contact.user_id;
          return (
            <li key={contact.user_id}>
              <article className={CONTACTS_PANEL_CLASSES.row} data-testid={`contact-row-${contact.user_id}`}>
                <span
                  className={CONTACTS_PANEL_CLASSES.avatar}
                  role="img"
                  aria-label={t("contacts.accessibility.avatarFallback", { name: accessibleName })}
                >
                  {avatarSource !== "" ? (
                    <img
                      src={avatarSource}
                      alt=""
                      onError={() => {
                        setFailedAvatarUsers((current) => {
                          if (current[contact.user_id] === true) {
                            return current;
                          }
                          return {
                            ...current,
                            [contact.user_id]: true,
                          };
                        });
                      }}
                    />
                  ) : (
                    deriveContactInitials(contact)
                  )}
                </span>
                <span className={CONTACTS_PANEL_CLASSES.content}>
                  <span className="contact-title">{title}</span>
                  <span className="contact-secondary">{secondary}</span>
                  {status !== "" ? <span className="contact-status">{status}</span> : null}
                </span>
                <span className={CONTACTS_PANEL_CLASSES.actions}>
                  <button
                    className="icon-button icon-button-primary"
                    type="button"
                    data-testid={`start-contact-chat-${contact.user_id}`}
                    aria-label={
                      starting
                        ? t("contacts.actions.startingChat", { name: accessibleName })
                        : t("contacts.actions.startChat", { name: accessibleName })
                    }
                    title={
                      starting
                        ? t("contacts.actions.startingChat", { name: accessibleName })
                        : t("contacts.actions.startChat", { name: accessibleName })
                    }
                    onClick={() => void onStartContactChat(contact)}
                    disabled={starting || removing}
                  >
                    <IconSend />
                  </button>
                  <button
                    className="icon-button icon-button-danger"
                    type="button"
                    data-testid={`remove-contact-${contact.user_id}`}
                    aria-label={
                      removing
                        ? t("contacts.actions.removing", { name: accessibleName })
                        : t("contacts.actions.remove", { name: accessibleName })
                    }
                    title={
                      removing
                        ? t("contacts.actions.removing", { name: accessibleName })
                        : t("contacts.actions.remove", { name: accessibleName })
                    }
                    onClick={() => void onRemoveContact(contact)}
                    disabled={removing || starting}
                  >
                    <IconTrash />
                  </button>
                </span>
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
